import React from "react";
import Tooltip from "../common/Tooltip";
import { InfoGlyph } from "./glyphs";

// "Agent commission split" — proposed split between the posting agent and the
// agent who brings the player (D-005). Optional, default Negotiable, display
// only (no platform calculation). First number = posting agent.
//
// V2 (approved 2026-07-21): three commission states —
//   • split filled          → card shows "X / Y"
//   • split empty           → card shows "Commission: Negotiable"
//   • no-commission switch  → card shows "No commission for this job";
//     the switch disables + clears the split fields (both request categories).
//
// V2 UI rebuild (doc 02): "Split" + grey "posting / responding · optional"
// label with the verbatim info-circle, two 76px boxes joined by "/" + "%",
// hint below; "Note (optional)" beside it (2-col ≥768); the no-commission
// switch as drawn (34×20 track, dashed top border).

// Verbatim artboard info-circle (RULE 2) — from the shared glyph set.
const IcInfo = () => <InfoGlyph size={12} className="text-[#94A3B8]" />;

const splitInputCls = (disabled) =>
  `h-10 w-[76px] text-center rounded-[9px] border text-[13.5px] outline-none [font-variant-numeric:tabular-nums] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-[#CBD5E1] ${
    disabled
      ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed"
      : "bg-white border-[#E2E8F0] focus:border-[#2283FF] focus:shadow-[0_0_0_3px_#E8F1FF]"
  }`;

const CommissionSplitField = ({
  posting,
  responding,
  note,
  noCommission = false,
  onChange,
}) => {
  const total =
    (posting === "" ? 0 : Number(posting)) +
    (responding === "" ? 0 : Number(responding));
  const showTotalHint =
    !noCommission && (posting !== "" || responding !== "") && total !== 100;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3.5">
        <div>
          <div className="flex items-center gap-[5px] mb-1.5">
            <span className="text-[12.5px] font-semibold text-[#334155]">
              Split{" "}
              <span className="font-medium text-[#94A3B8]">
                posting / responding · optional
              </span>
            </span>
            <Tooltip
              content="Proposed split between the posting agent and the agent who brings the player. First number = posting agent."
              position="top"
              className="whitespace-normal max-w-[240px]"
            >
              <span className="inline-flex cursor-default"><IcInfo /></span>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              value={posting}
              onChange={(e) => onChange({ posting: e.target.value })}
              placeholder="·"
              disabled={noCommission}
              aria-label="Posting agent %"
              className={splitInputCls(noCommission)}
            />
            <span className="text-[#94A3B8] font-bold">/</span>
            <input
              type="number"
              min="0"
              max="100"
              value={responding}
              onChange={(e) => onChange({ responding: e.target.value })}
              placeholder="·"
              disabled={noCommission}
              aria-label="Responding agent %"
              className={splitInputCls(noCommission)}
            />
            <span className="text-xs text-[#94A3B8]">%</span>
          </div>
          <p className="mt-[5px] text-[11.5px] text-[#94A3B8]">
            Leave empty to negotiate the split in chat · the card then shows
            "Commission: Negotiable".
          </p>
          {showTotalHint && (
            <p className="mt-1 text-[11.5px] text-[#B45309] font-semibold">
              The two shares should add up to 100%.
            </p>
          )}
        </div>
        <div>
          <div className="mb-1.5">
            <span className="text-[12.5px] font-semibold text-[#334155]">
              Note <span className="font-medium text-[#94A3B8]">(optional)</span>
            </span>
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => onChange({ note: e.target.value })}
            placeholder="Optional note about the commission terms."
            disabled={noCommission}
            aria-label="Split note"
            className={`h-10 w-full px-3 rounded-[9px] border text-[13px] outline-none placeholder:text-[#94A3B8] ${
              noCommission
                ? "bg-[#F1F5F9] border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed"
                : "bg-white border-[#E2E8F0] text-[#334155] focus:border-[#2283FF] focus:shadow-[0_0_0_3px_#E8F1FF]"
            }`}
          />
        </div>
      </div>
      {/* V2: explicit no-commission state — clears + disables the split above.
          The card renders it as "No commission for this job", never a silent
          blank. Switch drawn per artboard: 34×20 track, 16px knob. */}
      <label className="flex items-center gap-2.5 mt-3.5 pt-3 border-t border-dashed border-[#EEF2F7] cursor-pointer">
        <input
          type="checkbox"
          role="switch"
          checked={noCommission}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { noCommission: true, posting: "", responding: "", note: "" }
                : { noCommission: false },
            )
          }
          className="sr-only peer"
        />
        <span className="relative w-[34px] h-5 flex-none rounded-full bg-[#CBD5E1] transition-colors peer-checked:bg-[#2283FF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2283FF]/40 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:w-4 after:h-4 after:rounded-full after:bg-white after:shadow-[0_1px_2px_rgba(15,23,42,.2)] after:transition-transform peer-checked:after:translate-x-[14px]" />
        <span className="text-[13px] text-[#334155]">
          No commission possible for this job
        </span>
      </label>
    </div>
  );
};

export default CommissionSplitField;
